import { defineNuxtPlugin } from '#app'
import posthog from 'posthog-js'
import type { PostHog } from 'posthog-js'

export default defineNuxtPlugin(nuxtApp => {
  const runtimeConfig = useRuntimeConfig()
  const user = useSupabaseUser()
  
  // Skip initialization if no PostHog key is provided
  if (!runtimeConfig.public.posthogPublicKey) {
    console.warn('PostHog public key not found, skipping PostHog initialization')
    return {
      provide: {
        posthog: () => null as PostHog | null
      }
    }
  }
  
  const location = useRequestURL()

  // Function to identify user with PostHog (defined before initialization)
  const identifyUser = (userData: any, posthogInstance: PostHog) => {
    if (!userData || !posthogInstance) return
    
    const userId = userData.id
    const userProperties = {
      email: userData.email,
      name: userData.user_metadata?.full_name || userData.user_metadata?.name,
      created_at: userData.created_at,
      last_sign_in_at: userData.last_sign_in_at,
      app_metadata: userData.app_metadata,
      user_metadata: userData.user_metadata,
      // Add any additional user properties you want to track
      ...(userData.user_metadata || {})
    }
    
    posthogInstance.identify(userId, userProperties)
    console.log('PostHog user identified:', userId)
  }
  

  const isAdmin = computed(() => {
    return runtimeConfig.public.admins.includes(user.value?.id || '')
  })

  // Prepare PostHog configuration
  const posthogConfig = {
    api_host: runtimeConfig.public.posthogHost || 'https://app.posthog.com',
    person_profiles: 'always' as const,
    capture_pageview: false, // We'll handle pageviews manually with router
    disable_session_recording: location.hostname === "localhost" || location.hostname === "127.0.0.1" || isAdmin.value,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
          password: true,
          maskInputFn: (text, element) => {
            const maskTypes = ['email', 'password']
    
            if (
                maskTypes.indexOf(element?.attributes['type']?.value) !== -1 ||
                maskTypes.indexOf(element?.attributes['id']?.value) !== -1
            ) {
                return '*'.repeat(text.length)
            }
            return text
        },
        maskTextSelector: '*',
        maskTextFn(text) {
            // A simple email regex - you may want to use something more advanced
            const emailRegex = /(\S+)@(\S+\.\S+)/g
    
            return text.trim().replace(emailRegex, (match, g1, g2) => {
                // Replace each email with asterisks - ben@posthog.com becomes ***@***********
                return '*'.repeat(g1.length) + '@' + '*'.repeat(g2.length)
            })
        }
        }
      },
    loaded: (posthog: PostHog) => {
      if (import.meta.env.MODE === 'development') {
        posthog.debug()
        console.log('PostHog initialized in development mode')
      }
      
      // Identify user immediately if already authenticated
      if (user.value) {
        identifyUser(user.value, posthog)
      }
    },
    // Merge with additional defaults if they exist
    ...(typeof runtimeConfig.public.posthogDefaults === 'object' && runtimeConfig.public.posthogDefaults ? runtimeConfig.public.posthogDefaults : {})
  }
  
  const posthogClient = posthog.init(runtimeConfig.public.posthogPublicKey, posthogConfig)
  
  console.log('location', location)
  if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
    posthogClient.opt_out_capturing();
  }

  // Function to reset PostHog on logout
  const resetUser = () => {
    if (!posthogClient) return
    posthogClient.reset()
    console.log('PostHog user reset (logged out)')
  }

  // Watch for user state changes
  watch(user, (newUser, oldUser) => {
    if (newUser && newUser !== oldUser && posthogClient) {
      // User logged in or user data updated
      identifyUser(newUser, posthogClient)
    } else if (!newUser && oldUser) {
      // User logged out
      resetUser()
    }
  }, { immediate: false }) // Set to false since we handle initial identification in loaded callback

  // Handle page views with router
  const router = useRouter()
  router.afterEach((to) => {
    nextTick(() => {
      if (posthogClient) {
        posthogClient.capture('$pageview', {
          $current_url: to.fullPath
        })
      }
    })
  })

  // Provide PostHog instance globally
  return {
    provide: {
      posthog: () => posthogClient as PostHog | null
    }
  }
})